/* Cherished Lives — card carousel
   ---------------------------------------------------------------------------
   Turns a stack of cards into a horizontal, snap-scrolling rail. Shared by the
   B2B Partner Band, Yours to Control, CL Promise and How It Works sections so
   they all behave the same way as "The Viewer Experience".

   Markup contract:
     <div data-clcar data-clcar-max="899" data-clcar-autoplay="5000">
       <div data-clcar-rail>
         <div data-clcar-slide>…</div>
       </div>
       <div data-clcar-dots>
         <button type="button" data-clcar-dot="0">…</button>
       </div>
     </div>

   data-clcar-max       largest viewport the rail is active on (default 899)
   data-clcar-autoplay  advance interval in ms; omit for no autoplay

   How many cards are visible is read back from the rendered layout rather than
   configured here, so CSS alone decides 1-up / 2-up / 3-up per breakpoint. Any
   dot that would scroll past the end is hidden, and the whole dot row hides
   when everything already fits.

   Vertical scrolling is never intercepted. Touch panning is left entirely to
   the browser's native overflow scrolling, and only horizontal wheel/trackpad
   gestures and left-mouse drags are scripted.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  /* The asset is referenced by several sections — only wire things up once. */
  if (window.CLMobileCarousel) return;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function setup(root) {
    if (root.clcarReady) return;
    root.clcarReady = true;

    var rail = root.querySelector('[data-clcar-rail]');
    if (!rail) return;

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-clcar-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-clcar-dot]'));
    var dotsWrap = root.querySelector('[data-clcar-dots]');
    if (slides.length < 2) return;

    var maxw = parseInt(root.getAttribute('data-clcar-max'), 10) || 899;
    var autoplayMs = parseInt(root.getAttribute('data-clcar-autoplay'), 10) || 0;
    var mq = window.matchMedia('(max-width: ' + maxw + 'px)');
    var current = -1;

    /* Offset of a card inside the rail's scrollable content. Measured rather
       than taken from offsetLeft, which is relative to whichever ancestor
       happens to be positioned and so does not line up with scrollLeft. */
    function slideLeft(el) {
      return el.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft;
    }

    /* Distance from one card to the next, including the gap. */
    function stride() {
      if (slides.length > 1) {
        var d = slideLeft(slides[1]) - slideLeft(slides[0]);
        if (d > 0) return d;
      }
      return slides[0].offsetWidth || 1;
    }

    function perView() {
      return Math.max(1, Math.round(rail.clientWidth / stride()));
    }

    /* Furthest card index that can sit at the start of the rail. */
    function maxIndex() {
      return Math.max(0, slides.length - perView());
    }

    function setActive(idx) {
      if (idx === current) return;
      current = idx;
      /* Exposed so CSS can place things off the active slide — How It Works
         uses it to slide the ball along its connector line. */
      root.style.setProperty('--clcar-index', idx);
      root.dispatchEvent(new CustomEvent('clcar:change', {
        detail: { index: idx, count: slides.length }
      }));
      slides.forEach(function (el, i) { el.classList.toggle('is-active', i === idx); });
      dots.forEach(function (el, i) {
        el.classList.toggle('is-active', i === idx);
        if (i === idx) el.setAttribute('aria-current', 'true');
        else el.removeAttribute('aria-current');
      });
    }

    function nearest() {
      return Math.max(0, Math.min(maxIndex(), Math.round(rail.scrollLeft / stride())));
    }

    /* Scrolling is animated with rAF rather than behavior:'smooth'. A mandatory
       scroll-snap container will cancel a programmatic smooth scroll outright,
       which leaves the rail sitting still while the dots march on. Snapping is
       parked for the duration of the animation and restored at the end. */
    var animId = null;
    var animGuard = null;
    var snapBefore = null;

    function releaseSnap() {
      if (snapBefore !== null) {
        rail.style.scrollSnapType = snapBefore;
        snapBefore = null;
      }
    }

    function endAnim() {
      if (animId) { cancelAnimationFrame(animId); animId = null; }
      if (animGuard) { clearTimeout(animGuard); animGuard = null; }
    }

    function animateTo(left, instant) {
      var limit = rail.scrollWidth - rail.clientWidth;
      left = Math.max(0, Math.min(limit, left));

      endAnim();

      if (instant || reduced.matches) {
        releaseSnap();
        rail.scrollLeft = left;
        return;
      }

      var from = rail.scrollLeft;
      var delta = left - from;
      if (Math.abs(delta) < 1) { releaseSnap(); return; }

      if (snapBefore === null) snapBefore = rail.style.scrollSnapType;
      rail.style.scrollSnapType = 'none';

      var t0 = null;
      animId = requestAnimationFrame(function frame(t) {
        if (t0 === null) t0 = t;
        var p = Math.min(1, (t - t0) / 450);
        var e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        rail.scrollLeft = from + delta * e;
        if (p < 1) {
          animId = requestAnimationFrame(frame);
        } else {
          endAnim();
          releaseSnap();
        }
      });

      /* Frames stall in background tabs and throttled renderers. If that
         happens the card must still arrive, so land it outright. */
      animGuard = setTimeout(function () {
        endAnim();
        rail.scrollLeft = left;
        releaseSnap();
      }, 1400);
    }

    function goTo(idx, smooth) {
      idx = Math.max(0, Math.min(maxIndex(), idx));
      var el = slides[idx];
      if (!el) return;
      /* A one-up rail centres its card; a multi-up rail aligns to the left edge. */
      var left = perView() > 1
        ? slideLeft(el)
        : slideLeft(el) - (rail.clientWidth - el.clientWidth) / 2;
      animateTo(left, smooth === false);
      setActive(idx);
    }

    /* Hide any dot that cannot be reached, and the whole row if nothing scrolls. */
    function syncDots() {
      var max = maxIndex();
      dots.forEach(function (el, i) { el.style.display = i > max ? 'none' : ''; });
      if (dotsWrap) dotsWrap.style.display = max === 0 ? 'none' : '';
    }

    /* Keep the dots in step with whatever the rail is showing. */
    var ticking = false;
    rail.addEventListener('scroll', function () {
      if (animId) return;   /* a programmatic scroll already set the state */
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        setActive(nearest());
        ticking = false;
      });
    }, { passive: true });

    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        goTo(parseInt(dot.getAttribute('data-clcar-dot'), 10));
      });
    });

    /* Arrow keys move card by card once the rail has focus. */
    rail.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      goTo((current < 0 ? nearest() : current) + (e.key === 'ArrowRight' ? 1 : -1));
    });

    /* Horizontal wheel / two-finger trackpad swipe. A vertical wheel is ignored
       outright so the page carries on scrolling exactly as it did before. */
    rail.addEventListener('wheel', function (e) {
      if (!mq.matches) return;
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      if (rail.scrollWidth - rail.clientWidth <= 0) return;
      if (e.cancelable) e.preventDefault();
      rail.scrollLeft += e.deltaX;
    }, { passive: false });

    /* Mouse drag-to-scroll. Touch and pen are deliberately left alone: native
       overflow scrolling already handles them and keeps vertical panning free. */
    var dragging = false;
    var axis = null;
    var sx = 0;
    var sy = 0;
    var startLeft = 0;
    var moved = 0;

    rail.addEventListener('pointerdown', function (e) {
      if (!mq.matches) return;
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      /* Hand control straight to the pointer. */
      endAnim();
      releaseSnap();
      dragging = true;
      axis = null;
      moved = 0;
      sx = e.clientX;
      sy = e.clientY;
      startLeft = rail.scrollLeft;
    });

    window.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - sx;
      var dy = e.clientY - sy;

      if (axis === null) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        if (Math.abs(dx) > Math.abs(dy)) {
          axis = 'x';
          rail.classList.add('is-dragging');
        } else {
          /* Vertical intent — hand the gesture straight back to the page. */
          dragging = false;
          return;
        }
      }

      if (e.cancelable) e.preventDefault();
      moved = Math.abs(dx);
      rail.scrollLeft = startLeft - dx;
    }, { passive: false });

    function endDrag() {
      if (!dragging && !rail.classList.contains('is-dragging')) return;
      var wasHorizontal = axis === 'x';
      dragging = false;
      axis = null;
      rail.classList.remove('is-dragging');
      if (wasHorizontal) goTo(nearest());
    }
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    /* A drag must not fire the click on the card it started from. */
    rail.addEventListener('click', function (e) {
      if (moved > 8) {
        e.preventDefault();
        e.stopPropagation();
      }
      moved = 0;
    }, true);

    /* ----- autoplay ----------------------------------------------------- */
    var timer = null;
    var halted = false;

    function announce(name) {
      root.dispatchEvent(new CustomEvent(name, {
        detail: { index: current, count: slides.length, interval: autoplayMs }
      }));
    }

    function stop() {
      if (timer) {
        clearInterval(timer);
        timer = null;
        announce('clcar:stop');
      }
    }

    function play() {
      if (!autoplayMs || halted || reduced.matches) return;
      stop();
      announce('clcar:play');
      timer = setInterval(function () {
        var max = maxIndex();
        if (max <= 0) return;
        goTo(current >= max ? 0 : current + 1);
      }, autoplayMs);
    }

    /* Taking control stops it for good — WCAG 2.2.2 wants a way out of
       auto-moving content, and silently restarting fights the visitor. */
    function halt() {
      halted = true;
      stop();
    }

    if (autoplayMs) {
      rail.addEventListener('mouseenter', stop);
      rail.addEventListener('mouseleave', play);
      rail.addEventListener('focusin', stop);
      rail.addEventListener('focusout', play);
      rail.addEventListener('pointerdown', halt);
      rail.addEventListener('keydown', halt);
      dots.forEach(function (d) { d.addEventListener('click', halt); });

      /* Only run while the section is actually on screen. */
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (en.isIntersecting) play();
            else stop();
          });
        }, { threshold: 0.25 }).observe(root);
      } else {
        play();
      }
    }

    /* The rail is a scrollable region only where the carousel is active, so it
       is only focusable there — elsewhere it goes back to being a plain grid. */
    function onBreakpoint() {
      var scrolls = mq.matches && rail.scrollWidth - rail.clientWidth > 1;
      if (scrolls) rail.setAttribute('tabindex', '0');
      else rail.removeAttribute('tabindex');
      if (!mq.matches) rail.scrollLeft = 0;
      syncDots();
    }
    if (mq.addEventListener) mq.addEventListener('change', onBreakpoint);
    else if (mq.addListener) mq.addListener(onBreakpoint);

    var resizeTick;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTick);
      resizeTick = setTimeout(function () {
        syncDots();
        setActive(nearest());
      }, 150);
    }, { passive: true });

    onBreakpoint();
    setActive(0);
  }

  function init(scope) {
    var nodes = (scope || document).querySelectorAll('[data-clcar]');
    Array.prototype.forEach.call(nodes, setup);
  }

  window.CLMobileCarousel = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor re-renders sections in place. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
